// src/components/meeting/PreJoinScreen.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import AudioVisualizer from './AudioVisualizer';
import { setCookie, getCookie } from '../../utils/cookieUtils';

// Define quality presets for video constraints
const qualityLevels = {
    '144p': { width: { ideal: 256 }, height: { ideal: 144 }, frameRate: { ideal: 15 } },
    '360p': { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 30 } },
    '720p': { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    '1080p': { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
};

const PreJoinScreen = ({ onJoin, onCancel, meeting }) => {
    // State for device status and selection
    const [isMicOn, setIsMicOn] = useState(true);
    const [isCamOn, setIsCamOn] = useState(true);
    const [localStream, setLocalStream] = useState(null);
    const [devices, setDevices] = useState({ audio: [], video: [] });
    
    // Initialize state from cookies, defaulting to null if not set
    const [selectedAudio, setSelectedAudio] = useState(getCookie('preferredAudio') || null);
    const [selectedVideo, setSelectedVideo] = useState(getCookie('preferredVideo') || null);
    const [selectedQuality, setSelectedQuality] = useState(getCookie('preferredQuality') || '720p');

    // State for permissions and errors
    const [hasPermissions, setHasPermissions] = useState(false);
    const [error, setError] = useState('');

    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Get device permissions and list available devices
    const getDevicesAndPermissions = useCallback(async () => {
        try {
            // We request permissions here primarily to get the device labels.
            // If the user denies, we can still proceed.
            const permissionsStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            setHasPermissions(true);
            // Stop the tracks immediately as we will get a new stream later
            permissionsStream.getTracks().forEach(t => t.stop());
        } catch (err) {
            setError('Camera and microphone permissions are not granted. You can join as a listener.');
            setHasPermissions(false);
        }

        try {
            // Enumerate devices regardless of permissions
            const deviceInfos = await navigator.mediaDevices.enumerateDevices();
            const audioDevices = deviceInfos.filter(d => d.kind === 'audioinput');
            const videoDevices = deviceInfos.filter(d => d.kind === 'videoinput');
            setDevices({ audio: audioDevices, video: videoDevices });

            // If no device is selected (e.g., first visit), default to the first available device or 'none'
            if (selectedAudio === null) {
                setSelectedAudio(audioDevices[0]?.deviceId || 'none');
            }
            if (selectedVideo === null) {
                setSelectedVideo(videoDevices[0]?.deviceId || 'none');
            }
        } catch(err) {
            setError('Could not list your devices. You may not be able to send audio or video.');
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    // Get and display the media stream based on selections
    const getMedia = useCallback(async () => {
        // Stop any existing stream before getting a new one
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        const audioDevice = selectedAudio && selectedAudio !== 'none';
        const videoDevice = selectedVideo && selectedVideo !== 'none';

        // If no devices are selected, clear the stream and video element
        if (!audioDevice && !videoDevice) {
            setLocalStream(null);
            if (videoRef.current) videoRef.current.srcObject = null;
            return;
        }

        const quality = qualityLevels[selectedQuality];
        const constraints = {
            audio: audioDevice ? { deviceId: { exact: selectedAudio } } : false,
            video: videoDevice ? { deviceId: { exact: selectedVideo }, ...quality } : false,
        };

        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            setLocalStream(stream);

            if (videoRef.current) {
                // Only attach stream to video element if camera is on and a video device is selected
                videoRef.current.srcObject = isCamOn && videoDevice ? stream : null;
            }
        } catch (err) {
            setError('Could not access the selected camera or microphone. Please check if it is used by another application.');
        }
    }, [selectedAudio, selectedVideo, selectedQuality, isCamOn]);

    useEffect(() => {
        getDevicesAndPermissions();
    }, [getDevicesAndPermissions]);

    useEffect(() => {
        // We run getMedia whenever a device selection changes
        getMedia();
    }, [getMedia]);

    // Save preferences to cookies whenever they change
    useEffect(() => {
        setCookie('preferredAudio', selectedAudio, 365);
        setCookie('preferredVideo', selectedVideo, 365);
        setCookie('preferredQuality', selectedQuality, 365);
    }, [selectedAudio, selectedVideo, selectedQuality]);

    // Handle camera toggle, ensuring we don't show a black screen if no video device is selected
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.srcObject = isCamOn && selectedVideo !== 'none' ? localStream : null;
        }
    }, [isCamOn, localStream, selectedVideo]);


    const handleJoinClick = () => {
        // A stream is no longer required to join the meeting
        if (streamRef.current) {
            streamRef.current.getAudioTracks().forEach(track => (track.enabled = isMicOn));
            streamRef.current.getVideoTracks().forEach(track => (track.enabled = isCamOn));
        }
        // Pass the stream (which can be null) to the parent component
        onJoin(streamRef.current, selectedQuality);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f0f2f5', padding: '1rem' }}>
            <div style={{ width: '100%', maxWidth: '900px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '24px' }}>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '24px', textAlign: 'center' }}>Ready to join "{meeting.name}"?</h1>
                {error && <p style={{ color: '#ef4444', textAlign: 'center', marginBottom: '1rem' }}>{error}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', background: '#2C2F33', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                            {(isCamOn && selectedVideo !== 'none') ? (
                                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transform: 'scaleX(-1)' }} />
                            ) : (
                                <div style={{ color: 'white', fontSize: '1.25rem' }}>Camera is Off</div>
                            )}
                            {isMicOn && localStream && selectedAudio !== 'none' && <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', width: '80%' }}><AudioVisualizer stream={localStream} /></div>}
                        </div>
                        <div style={{ display: 'flex', gap: '16px' }}>
                             <button onClick={() => setIsMicOn(p => !p)} disabled={selectedAudio === 'none'} style={{ padding: '12px', borderRadius: '50%', background: isMicOn ? '#3b82f6' : '#ef4444', color: 'white', cursor: selectedAudio === 'none' ? 'not-allowed' : 'pointer', opacity: selectedAudio === 'none' ? 0.5 : 1 }} aria-label={`Microphone ${isMicOn ? 'On' : 'Off'}`}>Mic</button>
                             <button onClick={() => setIsCamOn(p => !p)} disabled={selectedVideo === 'none'} style={{ padding: '12px', borderRadius: '50%', background: isCamOn ? '#3b82f6' : '#ef4444', color: 'white', cursor: selectedVideo === 'none' ? 'not-allowed' : 'pointer', opacity: selectedVideo === 'none' ? 0.5 : 1 }} aria-label={`Camera ${isCamOn ? 'On' : 'Off'}`}>Cam</button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '16px' }}>
                        <div>
                            <label htmlFor="video-select" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Camera</label>
                            <select id="video-select" value={selectedVideo} onChange={e => setSelectedVideo(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} aria-label="Select Camera">
                                <option value="none">None</option>
                                {devices.video.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="audio-select" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Microphone</label>
                            <select id="audio-select" value={selectedAudio} onChange={e => setSelectedAudio(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} aria-label="Select Microphone">
                                <option value="none">None</option>
                                {devices.audio.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="quality-select" style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Call Quality</label>
                            <select id="quality-select" value={selectedQuality} onChange={e => setSelectedQuality(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} aria-label="Select Call Quality">
                                {Object.keys(qualityLevels).map(q => <option key={q} value={q}>{q}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                    <button onClick={onCancel} style={{ padding: '10px 24px', borderRadius: '4px', background: '#e5e7eb', color: '#374151' }}>Cancel</button>
                    <button onClick={handleJoinClick} style={{ padding: '10px 24px', borderRadius: '4px', background: '#16a34a', color: 'white', fontWeight: '600' }}>Join Meeting</button>
                </div>
            </div>
        </div>
    );
};

export default PreJoinScreen;
